import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, TextField, InputAdornment, MenuItem, Typography, Stack,
  Autocomplete, Tooltip, Button, alpha, Chip, Collapse, Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import * as actions from '../../../../redux/actions';
import {
  STATUS_LABELS, STATUS_ICONS, STATUS_OPTIONS, getStatusChipSx,
} from '../../constants/status';
import { TYPE_LABELS, TYPE_OPTIONS } from '../../constants/type';

const fieldSx = { minWidth: 160, bgcolor: 'background.paper', '& .MuiOutlinedInput-root': { borderRadius: 2 } };

// แถวเดียวของ panel "เพิ่มเติม" — ชื่อคอลัมน์ชิดซ้าย + ตัวควบคุมชิดขวา เรียงเป็นตารางเดียวกันทุกแถว
// (ก็อปสไตล์มาจาก ExpensesFilterPanel.js ของ Finance ตามที่ user ขอให้หน้าตาเหมือนกัน — ไม่ได้ import
// ใช้ร่วมกันเพราะคนละโดเมน field ไม่เหมือนกันเลย)
function FilterRow({ label, children, borderRight, borderBottom }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
        gap: { xs: 0.5, sm: 1.5 },
        alignItems: 'center',
        px: 1.75,
        py: 1.1,
        borderBottom: borderBottom ? '1px solid' : 'none',
        borderRight: { xs: 'none', sm: borderRight ? '1px solid' : 'none' },
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={700} color="text.primary">
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

// การ์ดตัวกรอง — ค้นหาแบบละเอียด (สถานะ/ประเภท/ผู้รับผิดชอบ/ลูกค้า/ช่วงวันครบกำหนดยื่นราคา)
// เรียงลำดับทำผ่านการคลิกหัวคอลัมน์ในตารางแทน (OpportunitiesTable.js) ไม่มี dropdown "เรียงตาม"
// ซ้ำในนี้แล้ว
export function OpportunityFilters({
  q, onQChange, statusFilter, onStatusFilterToggle,
  typeFilter, onTypeFilterChange, ownerFilter, onOwnerFilterChange, ownerOptions = [],
  contactFilter, onContactFilterChange,
  dueDateFrom, onDueDateFromChange, dueDateTo, onDueDateToChange,
  onClearFilters,
}) {
  const dispatch = useDispatch();
  const contact = useSelector((state) => state.contact);
  const [contactInput, setContactInput] = useState('');
  // "เพิ่มเติม" (Type/Owner/ลูกค้า/ช่วงวันครบกำหนดยื่นราคา) พับเก็บโดย default — state ล้วนๆ ของ
  // component นี้ ไม่ต้องยกขึ้นไปที่ parent เพราะเป็นแค่ UI แสดง/ซ่อน ไม่กระทบ query จริง
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ดึงลูกค้าทั้งหมดครั้งเดียว (ไม่ debounce ค้นหาทีละคำ+size:20 ต่อคำค้นแบบเดิม) — Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก contactInput
  useEffect(() => {
    dispatch(actions.contactAll({ contactType: 'client', size: 9999, page: 1 }));
  }, [dispatch]);

  const contactOptions = useMemo(() => contact?.rows || [], [contact?.rows]);

  const activeAdvancedCount = [typeFilter, ownerFilter, contactFilter, dueDateFrom, dueDateTo].filter(Boolean).length;
  const hasActiveFilters = !!(q || statusFilter.length > 0 || activeAdvancedCount > 0);

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <FilterAltOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>ตัวกรอง</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {hasActiveFilters && (
          <Tooltip title="ล้างตัวกรองทั้งหมด">
            <Button size="small" startIcon={<ClearAllIcon fontSize="small" />} onClick={onClearFilters} sx={{ borderRadius: 2 }}>
              ล้างตัวกรอง
            </Button>
          </Tooltip>
        )}
      </Stack>

      {/* filter สถานะแบบ chip — multi-select อิสระ (เลือกได้หลายอันพร้อมกัน) คลิกเพื่อ toggle
          เข้า/ออกทีละอัน ไม่เลือกอันไหนเลย = ไม่กรอง (โชว์ทุกสถานะ) */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {STATUS_OPTIONS.map((s) => {
          const Icon = STATUS_ICONS[s];
          const active = statusFilter.includes(s);
          return (
            <Chip
              key={s}
              size="small"
              label={STATUS_LABELS[s]}
              icon={Icon ? <Icon fontSize="small" /> : undefined}
              variant={active ? 'filled' : 'outlined'}
              onClick={() => onStatusFilterToggle(s)}
              sx={{ fontWeight: active ? 700 : 500, ...getStatusChipSx(s, active ? 'filled' : 'outlined') }}
            />
          );
        })}
      </Stack>

      {/* แถวหลัก — เหลือแค่ search + ปุ่มค้นหา + ปุ่มเพิ่มเติม + เรียงตาม (สไตล์เดียวกับ
          ExpensesFilterPanel.js ของเมนูรายการเบิกเงิน tab Success ตามที่ user ขอ) — Type/Owner/
          ลูกค้า/ช่วงวันครบกำหนดยื่นราคา ย้ายไปอยู่ใน panel "เพิ่มเติม" ด้านล่างแทน */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="ค้นหาเลขที่โครงการ, ชื่อโครงการ, รายละเอียด..."
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>,
          }}
          sx={{ ...fieldSx, minWidth: 200, flex: 1 }}
        />
        {/* ค้นหาเป็น live search (debounce ใน useOpportunities.js) อยู่แล้วตั้งแต่แรก — ปุ่มนี้แค่
            เพิ่มความคุ้นเคยแบบเดียวกับหน้าเบิกเงิน ไม่ได้ผูก handler แยกเพราะพิมพ์แล้ว apply ทันที */}
        <Button
          variant="contained"
          disableElevation
          startIcon={<SearchIcon />}
          sx={{
            borderRadius: 2,
            px: 2.5,
            flexShrink: 0,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 12px -3px rgba(99,102,241,0.5)',
            '&:hover': {
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 6px 16px -3px rgba(99,102,241,0.6)',
            },
          }}
        >
          ค้นหา
        </Button>
        <Badge badgeContent={activeAdvancedCount} color="primary" invisible={activeAdvancedCount === 0}>
          <Button
            size="small"
            variant={advancedOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s ease', transform: advancedOpen ? 'rotate(180deg)' : 'none' }} />}
            onClick={() => setAdvancedOpen((v) => !v)}
            sx={{ whiteSpace: 'nowrap', borderRadius: 2, flexShrink: 0 }}
          >
            เพิ่มเติม
          </Button>
        </Badge>
      </Stack>

      <Collapse in={advancedOpen}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Box
            sx={{
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.015)' : 'rgba(15,23,42,0.012)'),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            }}
          >
            <FilterRow label="Type" borderRight borderBottom>
              <TextField
                size="small" fullWidth select value={typeFilter}
                onChange={(e) => onTypeFilterChange(e.target.value)}
                sx={{ ...fieldSx, bgcolor: 'transparent' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CategoryOutlinedIcon fontSize="small" color="disabled" /></InputAdornment>,
                }}
              >
                <MenuItem value="">ทั้งหมด</MenuItem>
                {TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
                ))}
              </TextField>
            </FilterRow>
            <FilterRow label="Owner" borderBottom>
              <TextField
                size="small" fullWidth select value={ownerFilter}
                onChange={(e) => onOwnerFilterChange(e.target.value)}
                sx={{ ...fieldSx, bgcolor: 'transparent' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonOutlineOutlinedIcon fontSize="small" color="disabled" /></InputAdornment>,
                }}
              >
                <MenuItem value="">ทั้งหมด</MenuItem>
                {ownerOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            </FilterRow>
            <FilterRow label="ลูกค้า" borderRight>
              <Autocomplete
                size="small"
                options={contactOptions}
                value={contactFilter}
                onChange={(e, v) => onContactFilterChange(v)}
                inputValue={contactInput}
                onInputChange={(e, v) => setContactInput(v)}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(o, v) => (o._id || o.id) === (v._id || v.id)}
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField
                    {...params}
                    placeholder="เลือกลูกค้า"
                    sx={{ ...fieldSx, bgcolor: 'transparent' }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><ApartmentOutlinedIcon fontSize="small" color="disabled" /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FilterRow>
            <FilterRow label="ครบกำหนดยื่นราคา">
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small" fullWidth label="จาก" type="date"
                  value={dueDateFrom}
                  onChange={(e) => onDueDateFromChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...fieldSx, bgcolor: 'transparent' }}
                />
                <TextField
                  size="small" fullWidth label="ถึง" type="date"
                  value={dueDateTo}
                  onChange={(e) => onDueDateToChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...fieldSx, bgcolor: 'transparent' }}
                />
              </Stack>
            </FilterRow>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default OpportunityFilters;
